import { type ReactNode, type PointerEvent as ReactPointerEvent, useRef, useState } from "react";
import { AppIcon } from "../ui/icons";
import { useWindowStore, windowStore } from "./useWindowStore";
import {
  clampWindowGeom,
  type SnapSide,
  selectFocusedId,
  type Viewport,
  type WindowGeom,
  WM,
} from "./windowStore";
import styles from "./wm.module.css";

/**
 * Marco de ventana del WM propio (decisión C1). Patrón de rendimiento:
 * durante drag/resize se escribe transform/width/height DIRECTO en el DOM
 * (sin re-render); el store recibe la geometría final en pointerup. React
 * re-pinta una sola vez por gesto.
 */

const vp = (): Viewport => ({ vw: window.innerWidth, vh: window.innerHeight });

type ResizeMode = "e" | "s" | "se";

interface DragState {
  px: number;
  py: number;
  origin: WindowGeom;
}

interface Props {
  id: string;
  title: string;
  children: ReactNode;
  onClose?: () => void;
}

export function WindowFrame({ id, title, children, onClose }: Props) {
  const win = useWindowStore((s) => s.windows[id]);
  const focused = useWindowStore((s) => selectFocusedId(s) === id);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<DragState | null>(null);
  const resize = useRef<(DragState & { mode: ResizeMode }) | null>(null);
  const [snapPreview, setSnapPreview] = useState<SnapSide | null>(null);

  if (!win || win.minimized) return null;

  const paint = (g: WindowGeom) => {
    const el = frameRef.current;
    if (!el) return;
    el.style.transform = `translate3d(${g.x}px, ${g.y}px, 0)`;
    el.style.width = `${g.w}px`;
    el.style.height = `${g.h}px`;
  };

  // ── Drag por titlebar (deshabilitado si está maximizada, como un OS real) ──
  const onTitleDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button")) return; // los botones no arrastran
    windowStore.getState().focus(id);
    if (win.maximized) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, origin: { ...win } };
  };

  const dragGeom = (e: ReactPointerEvent): WindowGeom => {
    const d = drag.current;
    if (!d) return win;
    return clampWindowGeom(
      { ...d.origin, x: d.origin.x + e.clientX - d.px, y: d.origin.y + e.clientY - d.py },
      vp(),
    );
  };

  const onTitleMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    paint(dragGeom(e));
    const v = vp();
    setSnapPreview(
      e.clientX <= WM.EDGE_SNAP_PX ? "left" : e.clientX >= v.vw - WM.EDGE_SNAP_PX ? "right" : null,
    );
  };

  const onTitleUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const g = dragGeom(e);
    drag.current = null;
    const side = snapPreview;
    setSnapPreview(null);
    if (side) {
      windowStore.getState().snap(id, side, vp());
    } else {
      windowStore.getState().move(id, g.x, g.y, vp());
    }
  };

  // ── Resize por asas ──────────────────────────────────────────────────
  const onResizeDown = (mode: ResizeMode) => (e: ReactPointerEvent<HTMLDivElement>) => {
    windowStore.getState().focus(id);
    e.currentTarget.setPointerCapture(e.pointerId);
    e.stopPropagation();
    resize.current = { px: e.clientX, py: e.clientY, origin: { ...win }, mode };
  };

  const resizeGeom = (e: ReactPointerEvent): WindowGeom => {
    const r = resize.current;
    if (!r) return win;
    const dw = r.mode !== "s" ? e.clientX - r.px : 0;
    const dh = r.mode !== "e" ? e.clientY - r.py : 0;
    return clampWindowGeom({ ...r.origin, w: r.origin.w + dw, h: r.origin.h + dh }, vp());
  };

  const onResizeMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (resize.current) paint(resizeGeom(e));
  };

  const onResizeUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!resize.current) return;
    const g = resizeGeom(e);
    resize.current = null;
    windowStore.getState().resizeTo(id, g, vp());
  };

  return (
    <>
      {snapPreview && (
        <div
          className={`${styles.snapPreview} ${snapPreview === "left" ? styles.snapLeft : styles.snapRight}`}
        />
      )}
      <section
        ref={frameRef}
        className={[
          styles.frame,
          focused ? styles.focused : "",
          win.maximized ? styles.maximized : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          transform: `translate3d(${win.x}px, ${win.y}px, 0)`,
          width: win.w,
          height: win.h,
          zIndex: win.z,
        }}
        aria-label={title}
        data-testid={`win-${id}`}
        onPointerDown={() => windowStore.getState().focus(id)}
      >
        {/* biome-ignore lint/a11y/noStaticElementInteractions: superficie de drag del WM — los controles accesibles son los botones min/max/close */}
        <div
          className={styles.titlebar}
          onPointerDown={onTitleDown}
          onPointerMove={onTitleMove}
          onPointerUp={onTitleUp}
          onDoubleClick={() => windowStore.getState().toggleMaximize(id, vp())}
          data-testid={`titlebar-${id}`}
        >
          <span className={styles.titleIcon}>
            <AppIcon appId={id} size={15} />
          </span>
          <span className={styles.title}>{title}</span>
          <button
            type="button"
            className={styles.tbButton}
            aria-label={`Minimizar ${title}`}
            data-testid={`min-${id}`}
            onClick={() => windowStore.getState().toggleMinimize(id)}
          >
            –
          </button>
          <button
            type="button"
            className={styles.tbButton}
            aria-label={win.maximized ? `Restaurar ${title}` : `Maximizar ${title}`}
            data-testid={`max-${id}`}
            onClick={() => windowStore.getState().toggleMaximize(id, vp())}
          >
            {win.maximized ? "❐" : "▢"}
          </button>
          <button
            type="button"
            className={`${styles.tbButton} ${styles.tbClose}`}
            aria-label={`Cerrar ${title}`}
            data-testid={`close-${id}`}
            onClick={() => {
              windowStore.getState().close(id);
              onClose?.();
            }}
          >
            ×
          </button>
        </div>
        <div className={styles.content}>{children}</div>
        {!win.maximized && (
          <>
            <div
              className={styles.handleE}
              onPointerDown={onResizeDown("e")}
              onPointerMove={onResizeMove}
              onPointerUp={onResizeUp}
            />
            <div
              className={styles.handleS}
              onPointerDown={onResizeDown("s")}
              onPointerMove={onResizeMove}
              onPointerUp={onResizeUp}
            />
            <div
              className={styles.handleSE}
              onPointerDown={onResizeDown("se")}
              onPointerMove={onResizeMove}
              onPointerUp={onResizeUp}
            />
          </>
        )}
      </section>
    </>
  );
}
